import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { V2Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { PageBody } from '@abyss/web/ui/PageBody';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../common/AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils/getFeatureFlag';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent/useEducationalContent';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { FirstTimeUserBanner } from './common/FirstTimeUserBanner';
import { InformationalDrawer } from './common/InformationalDrawer';
import { toSlug } from './Guest.utils';
import { GuestFirstTimeUserModal } from './GuestFirstTimeUserModal';
import { InfoButtonStyle, StyleForHeader } from './GuestPlanSelection.style';

const LineOfBusinessStyledCard = styled(Card, {
  '&.abyss-card-root': {
    width: '180px',
    height: '343px',
    borderRadius: '8px',
    padding: '0px',
    marginTop: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
});

type LineOfBusinessCardProps = {
  title: string;
  description: string;
  icon: string;
  linkText: string;
  lob: string;
};

const LineOfBusinessCard = ({
  title,
  description,
  icon,
  linkText,
  lob,
}: LineOfBusinessCardProps) => {
  const { navigate } = useRouter();
  const url = '/select-coverage-type?planSelectionLob=' + lob;

  const handleClick = () => {
    adobeLinkTrackEvent({
      name: linkText,
      location: title,
      type: 'internal',
      destinationUrl: url,
    });
    navigate(url);
  };

  return (
    <LineOfBusinessStyledCard>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '0px',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Layout.Stack
          css={{
            'abyss-layout-stack': {
              padding: '16px',
              paddingBottom: '2px',
              alignItems: 'start',
            },
          }}
        >
          <IconBrand icon={icon} size="48px" />
          <Heading offset={6}>{title}</Heading>
          <Text
            css={{
              'abyss-text-root': {
                display: 'inline-block',
                lineHeight: '1.3 !important',
              },
            }}
            size={'$sm'}
          >
            {description}
          </Text>
        </Layout.Stack>
      </Card.Section>
      <Card.Section
        css={{
          'abyss-card-section': {
            padding: '16px',
            height: '52px',
            width: '180px',
            marginLeft: '0px',
            marginRight: '0px',
          },
        }}
      >
        <Layout.Group>
          <Link
            after={<IconSymbol icon="chevron_right" />}
            data-testid={`explore-${toSlug(lob || '')}-link`}
            isStandardAnchor
            onClick={() => handleClick()}
            size={'14px !important'}
          >
            {linkText}
          </Link>
        </Layout.Group>
      </Card.Section>
    </LineOfBusinessStyledCard>
  );
};

export const GuestLineOfBusinessPicker = () => {
  const { chooseType, setChooseType } = useGuestStore(
    useShallow((state: GuestState) => ({
      chooseType: state.chooseType,
      setChooseType: state.setChooseType,
    }))
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const lobs = useLagoon('lob')();
  const featureFlags = useLagoon('feature-flags')();

  lobs?.sort((lob) => lob.displayOrder);
  const lagoonUIMessage = useLagoon('ui-messaging')();

  const chooseMedicalCoverageType = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICAL_COVERAGE_TYPE,
  })?.message;
  const typeOfMedicalCoverageHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE_HEADING,
  })?.message;
  const signInMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_MESSAGE,
  })?.message;
  const signInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.message;
  const signInUrl = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.url;
  const exploreCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.EXPLORE_COVERAGE,
  })?.message;

  const typeOfMedicalCoverageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.TYPE_OF_MEDICAL_COVERAGE,
  })?.message;

  const isFirstTimeUserBannerEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED
  );

  const bannerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .LOB_PICKER_FIRST_TIME_USER_BANNER_TEXT,
  })?.message;

  const drawerHeaderText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_DRAWER_HEADER,
  })?.message;

  const drawerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOB_PICKER_DRAWER_CONTENT,
  })?.message;

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const [shouldShowFirstTimeUserBanner, setShouldShowFirstTimeUserBanner] =
    useState(false);

  const [saveEducationalContent, setEducationalContentStatus] =
    useSaveEducationalContentStatus({});

  useEffect(() => {
    if (
      educationalContentStore?.hasViewedGuestLobBanner === false &&
      isFirstTimeUserBannerEnabled
    ) {
      setShouldShowFirstTimeUserBanner(true);
      setEducationalContentStatus({
        variables: {
          ...educationalContentStore,
          hasViewedGuestLobBanner: true,
        },
      });
    }
  }, [educationalContentStore?.hasViewedGuestLobBanner]);

  const handleOnLearnMoreClick = () => {
    setIsDrawerOpen(true);
  };

  const handleOnClose = () => {
    setShouldShowFirstTimeUserBanner(false);
    setEducationalContentStore(
      saveEducationalContent.data.saveEducationalContentStatus
    );
  };

  useEffect(() => {
    if (chooseMedicalCoverageType) {
      setChooseType(chooseMedicalCoverageType);
    }
  }, [chooseMedicalCoverageType, setChooseType]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: `guest plan selection`,
    isGuest: true,
  });

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  return (
    <>
      <GuestFirstTimeUserModal />
      <Box color="$bgHeaderColor" css={StyleForHeader}>
        {typeOfMedicalCoverageText}
        <Heading css={{ marginTop: '8px' }} offset={1}>
          {chooseType}
        </Heading>
      </Box>
      {shouldShowFirstTimeUserBanner && (
        <FirstTimeUserBanner
          content={bannerContentText}
          onClose={handleOnClose}
          onLearnMoreClick={handleOnLearnMoreClick}
        />
      )}
      <PageBody>
        <Flex
          alignContent="center"
          css={{ marginTop: '32px', gap: '6px' }}
          direction="row"
          justify="center"
        >
          <Heading data-testid="lob-selection" offset={3} textAlign="center">
            {typeOfMedicalCoverageHeadingText}
          </Heading>
          <V2Button
            css={InfoButtonStyle}
            data-testid="info-button"
            onClick={() => setIsDrawerOpen(true)}
            style={{ alignSelf: 'center' }}
            variant="text"
          >
            <IconSymbol icon="info" size={16} variant="outlined" />
          </V2Button>
        </Flex>
        <Flex
          alignItems="baseline"
          css={{ marginTop: '8px' }}
          direction="row"
          justify="center"
        >
          <Text>{signInMessageText}</Text>
          <Link
            data-testid="sign-in-link"
            href={signInUrl}
            onClick={() => {
              adobeLinkTrackEvent({
                name: signInText,
                location: chooseType,
                type: 'internal',
                destinationUrl: signInUrl,
              });
            }}
            openNewWindow={false}
            style={{ marginLeft: '4px' }}
          >
            {signInText}
          </Link>
        </Flex>

        <Flex
          alignItems="baseline"
          direction="row"
          justify="center"
          style={{ gap: '24px', marginTop: '32px' }}
        >
          {lobs?.map((lob) => (
            <LineOfBusinessCard
              description={lob.description}
              icon={lob.dntIcon}
              key={lob.key}
              linkText={exploreCoverageText}
              lob={lob.key}
              title={lob.displayName}
            />
          ))}
        </Flex>
        <InformationalDrawer
          content={drawerContentText}
          header={drawerHeaderText}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      </PageBody>
    </>
  );
};
